/**
 * Auth Context Provider
 * Handles authentication state and API calls
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTenant } from './useTenant';

// ============================================================================
// Types
// ============================================================================

interface User {
  id: number;
  email: string;
  name: string;
  is_platform_admin: boolean;
}

interface TenantInfo {
  id: number;
  slug: string;
  name: string;
  role: string;
  tier?: string;
}

interface AuthContextType {
  user: User | null;
  tenants: TenantInfo[];
  currentTenant: TenantInfo | null;
  role: string | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, tenantSlug?: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  switchTenant: (tenantId: number) => Promise<void>;
  refreshToken: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  company_name: string;
  tenant_slug?: string;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ============================================================================
// API Helper
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || '';

async function apiCall(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('access_token');
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {})
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  
  return data;
}

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [currentTenant, setCurrentTenant] = useState<TenantInfo | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('access_token'));
  const [isLoading, setIsLoading] = useState(true);

  const { setTenant } = useTenant();

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    const token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (!token) {
      setIsLoading(false);
      return;
    }
    
    try {
      // Try to get current user
      const data = await apiCall('/api/auth/me');
      
      setUser(data.user);
      setTenants(data.tenants || []);
      setRole(data.role);
      
      if (data.current_tenant) {
        setCurrentTenant({
          id: data.current_tenant.id,
          slug: data.current_tenant.slug,
          name: data.current_tenant.name,
          role: data.role
        });
        setTenant(data.current_tenant);
      }
    } catch (err) {
      // Token might be expired, try refresh
      if (refreshToken) {
        try {
          await refreshTokenInternal();
        } catch {
          // Refresh failed, clear tokens
          clearAuth();
        }
      } else {
        clearAuth();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = useCallback(async (email: string, password: string, tenantSlug?: string) => {
    setIsLoading(true);
    
    try {
      const data = await apiCall('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, tenant_slug: tenantSlug })
      });
      
      // Store tokens
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setToken(data.access_token);

      // Set state
      setUser(data.user);
      setTenants(data.tenants || []);
      setRole(data.role);

      if (data.current_tenant) {
        setCurrentTenant({
          id: data.current_tenant.id,
          slug: data.current_tenant.slug,
          name: data.current_tenant.name,
          role: data.role || 'drafter'
        });
        setTenant(data.current_tenant);
      }
    } finally {
      setIsLoading(false);
    }
  }, [setTenant]);

  const register = useCallback(async (data: RegisterData) => {
    setIsLoading(true);

    try {
      const result = await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(data)
      });

      // Store tokens
      localStorage.setItem('access_token', result.access_token);
      localStorage.setItem('refresh_token', result.refresh_token);
      setToken(result.access_token);

      // Set state
      setUser(result.user);
      setTenants(result.tenants || []);
      
      if (result.current_tenant) {
        setCurrentTenant({
          id: result.current_tenant.id,
          slug: result.current_tenant.slug,
          name: result.current_tenant.name,
          role: 'owner'
        });
        setRole('owner');
        setTenant(result.current_tenant);
      }
    } finally {
      setIsLoading(false);
    }
  }, [setTenant]);

  const logout = useCallback(() => {
    clearAuth();
    window.location.href = '/login';
  }, []);

  const switchTenant = useCallback(async (tenantId: number) => {
    try {
      const data = await apiCall('/api/auth/switch-tenant', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId })
      });
      
      // Update token
      localStorage.setItem('access_token', data.access_token);
      
      if (data.current_tenant) {
        setCurrentTenant({
          id: data.current_tenant.id,
          slug: data.current_tenant.slug,
          name: data.current_tenant.name,
          role: role || 'drafter'
        });
        setTenant(data.current_tenant);
      }
      
      // Reload to refresh all data
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch tenant:', err);
      throw err;
    }
  }, [role, setTenant]);

  const refreshTokenInternal = async () => {
    const storedRefreshToken = localStorage.getItem('refresh_token');

    if (!storedRefreshToken) {
      throw new Error('No refresh token');
    }

    const data = await apiCall('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: storedRefreshToken })
    });

    localStorage.setItem('access_token', data.access_token);
    setToken(data.access_token);

    // Re-fetch user data
    await checkSession();
  };

  const refreshToken = useCallback(async () => {
    await refreshTokenInternal();
  }, []);

  const clearAuth = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    setTenants([]);
    setCurrentTenant(null);
    setRole(null);
  };

  const value: AuthContextType = {
    user,
    tenants,
    currentTenant,
    role,
    token,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    switchTenant,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
